// ============================================================
// KI-Anbieter-Abstraktion für den KI-Assistenten.
// Der Assistent kann mit JEDEM gängigen Anbieter laufen — je Anbieter
// ist ein „gut & günstig"-Modell voreingestellt (⭐). Alle bis auf
// Anthropic sprechen das OpenAI-kompatible /chat/completions-Format.
//
// Konfiguration (lokal, NICHT synchronisiert):
//   amed_ai_provider      = aktive Anbieter-ID
//   amed_ai_model         = gewähltes Modell (leer = Empfehlung des Anbieters)
//   amed_ai_key_<id>      = API-Key je Anbieter
//   (Anthropic teilt sich den Key mit der Dokumenten-KI: amed_anthropic_api_key)
// ============================================================

export type ProviderFormat = 'openai' | 'anthropic';

export interface AiModel {
  id: string;
  label: string;
}

export interface AiProvider {
  id: string;
  label: string;
  format: ProviderFormat;
  endpoint: string;
  models: AiModel[];       // erstes Modell = Empfehlung (gut & günstig)
  keyUrl: string;          // wo man den Key bekommt
  hint?: string;
}

export const PROVIDERS: AiProvider[] = [
  {
    id: 'openrouter',
    label: 'OpenRouter — alle Anbieter über 1 Key',
    format: 'openai',
    endpoint: 'https://openrouter.ai/api/v1/chat/completions',
    models: [
      { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash — ⭐ gut & sehr günstig' },
      { id: 'deepseek/deepseek-chat', label: 'DeepSeek Chat — sehr günstig' },
      { id: 'openai/gpt-4o-mini', label: 'GPT-4o mini' },
      { id: 'anthropic/claude-3.5-haiku', label: 'Claude 3.5 Haiku' },
      { id: 'meta-llama/llama-3.3-70b-instruct', label: 'Llama 3.3 70B' },
    ],
    keyUrl: 'openrouter.ai/keys',
    hint: 'Empfohlen: ein Key, Zugriff auf praktisch alle Modelle. Läuft zuverlässig im Browser.',
  },
  {
    id: 'anthropic',
    label: 'Anthropic (Claude)',
    format: 'anthropic',
    endpoint: 'https://api.anthropic.com/v1/messages',
    models: [
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — ⭐ schnell & günstig' },
      { id: 'claude-sonnet-5', label: 'Claude Sonnet 5 — stärker' },
    ],
    keyUrl: 'console.anthropic.com',
  },
  {
    id: 'openai',
    label: 'OpenAI (GPT)',
    format: 'openai',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    models: [
      { id: 'gpt-4o-mini', label: 'GPT-4o mini — ⭐ günstig' },
      { id: 'gpt-4o', label: 'GPT-4o — stärker' },
    ],
    keyUrl: 'platform.openai.com/api-keys',
  },
  {
    id: 'google',
    label: 'Google (Gemini)',
    format: 'openai',
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/openai/chat/completions',
    models: [
      { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash — ⭐ günstig' },
      { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro — stärker' },
    ],
    keyUrl: 'aistudio.google.com/apikey',
  },
  {
    id: 'deepseek',
    label: 'DeepSeek',
    format: 'openai',
    endpoint: 'https://api.deepseek.com/v1/chat/completions',
    models: [
      { id: 'deepseek-chat', label: 'DeepSeek Chat — ⭐ sehr günstig' },
    ],
    keyUrl: 'platform.deepseek.com',
  },
  {
    id: 'groq',
    label: 'Groq — sehr schnell',
    format: 'openai',
    endpoint: 'https://api.groq.com/openai/v1/chat/completions',
    models: [
      { id: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B — ⭐ günstig' },
    ],
    keyUrl: 'console.groq.com/keys',
  },
  {
    id: 'mistral',
    label: 'Mistral',
    format: 'openai',
    endpoint: 'https://api.mistral.ai/v1/chat/completions',
    models: [
      { id: 'mistral-small-latest', label: 'Mistral Small — ⭐ günstig' },
    ],
    keyUrl: 'console.mistral.ai',
  },
];

const DEFAULT_PROVIDER = 'openrouter';

export function getProvider(id: string): AiProvider {
  return PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0]!;
}

/** Empfohlenes (erstes) Modell eines Anbieters. */
export function recommendedModel(p: AiProvider): string {
  return p.models[0]!.id;
}

function keyStorageName(providerId: string): string {
  // Anthropic teilt sich den Key mit der Dokumenten-KI (Vision).
  return providerId === 'anthropic' ? 'amed_anthropic_api_key' : `amed_ai_key_${providerId}`;
}

export function getProviderKey(providerId: string): string | null {
  const v = localStorage.getItem(keyStorageName(providerId));
  return v && v.trim() !== '' ? v.trim() : null;
}

export function setProviderKey(providerId: string, key: string): void {
  const name = keyStorageName(providerId);
  const trimmed = key.trim();
  if (trimmed !== '') localStorage.setItem(name, trimmed);
  else localStorage.removeItem(name);
}

export function getActiveProviderId(): string {
  return localStorage.getItem('amed_ai_provider') ?? DEFAULT_PROVIDER;
}
export function setActiveProviderId(id: string): void {
  localStorage.setItem('amed_ai_provider', id);
}

/** Von Anbietern abgeschaltete Modelle → werden ignoriert und durch die
 *  aktuelle Empfehlung ersetzt (heilt alte gespeicherte Auswahl automatisch). */
const DEPRECATED_MODELS = new Set<string>([
  'gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro',
  'google/gemini-2.0-flash-001', 'google/gemini-2.0-flash',
]);

/** Roh gespeichertes Modell (leer = „Empfehlung"); veraltete werden geleert. */
export function getStoredModel(): string {
  const s = (localStorage.getItem('amed_ai_model') ?? '').trim();
  if (s !== '' && DEPRECATED_MODELS.has(s)) {
    localStorage.removeItem('amed_ai_model');
    return '';
  }
  return s;
}

export function getActiveModel(): string {
  const stored = getStoredModel();
  if (stored !== '') return stored;
  return recommendedModel(getProvider(getActiveProviderId()));
}
export function setActiveModel(model: string): void {
  const trimmed = model.trim();
  if (trimmed !== '' && !DEPRECATED_MODELS.has(trimmed)) localStorage.setItem('amed_ai_model', trimmed);
  else localStorage.removeItem('amed_ai_model');
}

export interface AiConfig {
  provider: AiProvider;
  model: string;
  key: string | null;
}

export function getAiConfig(): AiConfig {
  const provider = getProvider(getActiveProviderId());
  return { provider, model: getActiveModel(), key: getProviderKey(provider.id) };
}

/** Ob der aktuell gewählte Anbieter einen Key hat (Assistent nutzbar). */
export function isAiConfigured(): boolean {
  return getAiConfig().key !== null;
}

interface ChatMsg { role: 'user' | 'assistant'; content: string; }

/** Einheitlicher Chat-Aufruf über den aktiven Anbieter. */
export async function aiChat(system: string, messages: ChatMsg[]): Promise<string> {
  const { provider, model, key } = getAiConfig();
  if (key === null) {
    throw new Error(`Kein API-Key für ${provider.label} hinterlegt (Einstellungen → KI-Assistent).`);
  }
  return provider.format === 'anthropic'
    ? callAnthropic(provider, model, key, system, messages)
    : callOpenAiCompatible(provider, model, key, system, messages);
}

async function callAnthropic(
  provider: AiProvider, model: string, key: string, system: string, messages: ChatMsg[],
): Promise<string> {
  const res = await doFetch(provider.endpoint, {
    'x-api-key': key,
    'anthropic-version': '2023-06-01',
    'content-type': 'application/json',
    'anthropic-dangerous-direct-browser-access': 'true',
  }, JSON.stringify({ model, max_tokens: 1500, system, messages }), provider);

  const data = await res.json() as { content: Array<{ type: string; text?: string }> };
  return data.content.find((c) => c.type === 'text')?.text ?? '(keine Antwort)';
}

async function callOpenAiCompatible(
  provider: AiProvider, model: string, key: string, system: string, messages: ChatMsg[],
): Promise<string> {
  const headers: Record<string, string> = {
    'authorization': `Bearer ${key}`,
    'content-type': 'application/json',
  };
  // OpenRouter empfiehlt diese Header (optional, aber gern gesehen).
  if (provider.id === 'openrouter') {
    headers['HTTP-Referer'] = 'https://amed-app.pages.dev';
    headers['X-Title'] = 'AMed App';
  }
  const body = JSON.stringify({
    model,
    max_tokens: 1500,
    messages: [{ role: 'system', content: system }, ...messages],
  });
  const res = await doFetch(provider.endpoint, headers, body, provider);
  const data = await res.json() as { choices?: Array<{ message?: { content?: string } }> };
  return data.choices?.[0]?.message?.content ?? '(keine Antwort)';
}

async function doFetch(
  url: string, headers: Record<string, string>, body: string, provider: AiProvider,
): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', headers, body });
  } catch (e) {
    throw new Error(
      `Netzwerkfehler bei ${provider.label}: ${e instanceof Error ? e.message : String(e)}. ` +
      `Falls der Anbieter Browser-Zugriff blockiert, OpenRouter verwenden.`,
    );
  }
  if (!res.ok) {
    if (res.status === 401) throw new Error(`Ungültiger API-Key für ${provider.label}.`);
    if (res.status === 429) throw new Error('API-Limit erreicht — kurz warten.');
    const t = await res.text().catch(() => '');
    throw new Error(`KI-Fehler ${res.status} (${provider.label}): ${t.slice(0, 180)}`);
  }
  return res;
}
